import type { Feature, FeatureCollection, LineString, Point } from "geojson";
import {
  AttributionControl,
  GeoJSONSource,
  LngLatBounds,
  Map as MapLibreMap,
  type MapLayerMouseEvent,
  NavigationControl,
} from "maplibre-gl";
import { Focus } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { LIGHT_MAP_STYLE, PLACE_LABEL_LAYER_ID } from "../mapStyle";
import type { Direction, Flow, OdData } from "../types";
import { compactTrips, counterpartName, flowCurve, regionByName, stableOffset } from "../utils";

interface FlowMapProps {
  data: OdData;
  direction: Direction;
  flows: Flow[];
  flowLimit: number;
  selected: string[];
  hoveredRegion: string | null;
  onHoverRegion: (name: string | null) => void;
  onSelectRegion: (name: string) => void;
}

interface FlowFeatureProperties {
  id: string;
  origin: string;
  destination: string;
  counterpart: string;
  trips: number;
  normalized: number;
}

function geojsonBounds(data: OdData): LngLatBounds {
  const bounds = new LngLatBounds();
  const visit = (value: unknown) => {
    if (!Array.isArray(value)) return;
    if (value.length >= 2 && typeof value[0] === "number" && typeof value[1] === "number") {
      bounds.extend([value[0], value[1]]);
      return;
    }
    value.forEach(visit);
  };
  data.geojson.features.forEach((feature) => visit(feature.geometry.coordinates));
  return bounds;
}

function arrowImage(): ImageData {
  const canvas = document.createElement("canvas");
  canvas.width = 32;
  canvas.height = 32;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas is unavailable");
  context.clearRect(0, 0, 32, 32);
  context.fillStyle = "#c92843";
  context.strokeStyle = "rgba(255,255,255,.94)";
  context.lineWidth = 2;
  context.beginPath();
  context.moveTo(16, 3);
  context.lineTo(27, 24);
  context.lineTo(16, 20);
  context.lineTo(5, 24);
  context.closePath();
  context.fill();
  context.stroke();
  return context.getImageData(0, 0, 32, 32);
}

function bearingAt(points: Array<[number, number]>, index: number): number {
  const start = points[Math.max(0, index - 1)];
  const end = points[Math.min(points.length - 1, index + 1)];
  const latitudeScale = Math.cos(((start[1] + end[1]) / 2) * Math.PI / 180);
  const dx = (end[0] - start[0]) * latitudeScale;
  const dy = end[1] - start[1];
  return Math.atan2(dx, dy) * 180 / Math.PI;
}

export function FlowMap({
  data,
  direction,
  flows,
  flowLimit,
  selected,
  hoveredRegion,
  onHoverRegion,
  onSelectRegion,
}: FlowMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const animationRef = useRef<number | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [hoveredFlow, setHoveredFlow] = useState<FlowFeatureProperties | null>(null);
  const lookup = useMemo(() => regionByName(data.regions), [data.regions]);
  const airport = lookup.get(data.meta.airportRegion);
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const visibleFlows = useMemo(() => flows.slice(0, flowLimit), [flowLimit, flows]);
  const maximum = Math.max(1, ...visibleFlows.map((flow) => flow.t));

  const flowFeatures = useMemo(() => {
    if (!airport) return [];
    return visibleFlows.flatMap((flow): Array<Feature<LineString, FlowFeatureProperties>> => {
      const counterpart = counterpartName(flow, direction);
      const region = lookup.get(counterpart);
      if (!region) return [];
      const start = direction === "to" ? region.center : airport.center;
      const end = direction === "to" ? airport.center : region.center;
      return [{
        type: "Feature",
        properties: {
          id: `${flow.o}\u001f${flow.d}`,
          origin: flow.o,
          destination: flow.d,
          counterpart,
          trips: flow.t,
          normalized: flow.t / maximum,
        },
        geometry: { type: "LineString", coordinates: flowCurve(start, end) },
      }];
    });
  }, [airport, direction, lookup, maximum, visibleFlows]);

  const flowGeojson = useMemo<FeatureCollection<LineString, FlowFeatureProperties>>(
    () => ({ type: "FeatureCollection", features: flowFeatures }),
    [flowFeatures],
  );

  const decoratedRegions = useMemo(() => {
    const active = new Map<string, number>();
    visibleFlows.forEach((flow) => active.set(counterpartName(flow, direction), flow.t));
    return {
      type: "FeatureCollection" as const,
      features: data.geojson.features.map((feature) => {
        const name = String(feature.properties?.name ?? "");
        const isAirport = name === data.meta.airportRegion;
        const value = active.get(name) ?? 0;
        return {
          ...feature,
          properties: {
            ...feature.properties,
            isAirport,
            active: value > 0,
            selected: selectedSet.has(name),
            hovered: hoveredRegion === name,
            normalized: value / maximum,
          },
        };
      }),
    };
  }, [data.geojson.features, data.meta.airportRegion, direction, hoveredRegion, maximum, selectedSet, visibleFlows]);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new MapLibreMap({
      container: containerRef.current,
      style: LIGHT_MAP_STYLE,
      bounds: geojsonBounds(data),
      fitBoundsOptions: { padding: 44 },
      minZoom: 8,
      maxZoom: 16,
      attributionControl: false,
    });
    mapRef.current = map;
    map.addControl(new AttributionControl({ compact: true }), "bottom-left");
    map.addControl(new NavigationControl({ showCompass: false }), "bottom-right");

    const regionName = (event: MapLayerMouseEvent) => String(event.features?.[0]?.properties?.name ?? "");
    map.on("load", () => {
      map.addImage("flow-arrow", arrowImage(), { pixelRatio: 2 });
      map.addSource("regions", { type: "geojson", data: decoratedRegions, promoteId: "name" });
      map.addLayer({
        id: "region-fill",
        type: "fill",
        source: "regions",
        paint: {
          "fill-color": [
            "case",
            ["boolean", ["get", "isAirport"], false], "#c92843",
            ["boolean", ["get", "selected"], false], "#af2139",
            ["boolean", ["get", "active"], false],
            ["interpolate", ["linear"], ["get", "normalized"], 0, "#f4e3e6", 1, "#df6a7d"],
            "#e4e7e5",
          ],
          "fill-opacity": ["case", ["boolean", ["get", "isAirport"], false], 0.88, 0.58],
        },
      }, PLACE_LABEL_LAYER_ID);
      map.addLayer({
        id: "region-outline",
        type: "line",
        source: "regions",
        paint: {
          "line-color": [
            "case",
            ["boolean", ["get", "hovered"], false], "#1e272d",
            ["boolean", ["get", "isAirport"], false], "#7d1327",
            ["boolean", ["get", "selected"], false], "#7d1327",
            "#6b767c",
          ],
          "line-width": [
            "case",
            ["boolean", ["get", "hovered"], false], 2.25,
            ["boolean", ["get", "isAirport"], false], 1.8,
            ["boolean", ["get", "selected"], false], 1.55,
            0.72,
          ],
          "line-opacity": 0.82,
        },
      }, PLACE_LABEL_LAYER_ID);
      map.addSource("flows", { type: "geojson", data: flowGeojson });
      map.addLayer({
        id: "flow-lines",
        type: "line",
        source: "flows",
        paint: {
          "line-color": "#c92843",
          "line-opacity": ["interpolate", ["linear"], ["get", "normalized"], 0, 0.32, 1, 0.82],
          "line-width": ["interpolate", ["linear"], ["get", "normalized"], 0, 0.8, 1, 4.6],
        },
        layout: { "line-cap": "round", "line-join": "round" },
      }, PLACE_LABEL_LAYER_ID);
      map.addLayer({
        id: "flow-hit",
        type: "line",
        source: "flows",
        paint: { "line-color": "rgba(0,0,0,0)", "line-width": 12 },
      }, PLACE_LABEL_LAYER_ID);
      map.addSource("flow-particles", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "flow-particles",
        type: "symbol",
        source: "flow-particles",
        layout: {
          "icon-image": "flow-arrow",
          "icon-size": ["interpolate", ["linear"], ["get", "normalized"], 0, 0.34, 1, 0.5],
          "icon-rotate": ["get", "bearing"],
          "icon-rotation-alignment": "map",
          "icon-allow-overlap": true,
        },
        paint: { "icon-opacity": 0.96 },
      }, PLACE_LABEL_LAYER_ID);

      map.on("mouseenter", "region-fill", (event) => {
        map.getCanvas().style.cursor = "pointer";
        const name = regionName(event);
        if (name) onHoverRegion(name);
      });
      map.on("mouseleave", "region-fill", () => {
        map.getCanvas().style.cursor = "";
        onHoverRegion(null);
      });
      map.on("click", "region-fill", (event) => {
        const name = regionName(event);
        if (name && name !== data.meta.airportRegion) onSelectRegion(name);
      });
      map.on("mouseenter", "flow-hit", (event) => {
        map.getCanvas().style.cursor = "crosshair";
        const properties = event.features?.[0]?.properties as FlowFeatureProperties | undefined;
        if (properties) setHoveredFlow(properties);
      });
      map.on("mousemove", "flow-hit", (event) => {
        const properties = event.features?.[0]?.properties as FlowFeatureProperties | undefined;
        if (properties) setHoveredFlow(properties);
      });
      map.on("mouseleave", "flow-hit", () => {
        map.getCanvas().style.cursor = "";
        setHoveredFlow(null);
      });
      setMapLoaded(true);
    });

    const observer = new ResizeObserver(() => map.resize());
    observer.observe(containerRef.current);
    return () => {
      observer.disconnect();
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      map.remove();
      mapRef.current = null;
    };
    // Geometry is immutable for this single-data app. Live updates use sources.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    (map.getSource("regions") as GeoJSONSource | undefined)?.setData(decoratedRegions);
    (map.getSource("flows") as GeoJSONSource | undefined)?.setData(flowGeojson);
  }, [decoratedRegions, flowGeojson, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!mapLoaded || !map) return;
    const source = map.getSource("flow-particles") as GeoJSONSource | undefined;
    if (!source) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const updateParticles = (now: number) => {
      const features: Array<Feature<Point, { bearing: number; normalized: number }>> = [];
      flowFeatures.slice(0, 24).forEach((feature) => {
        const coordinates = feature.geometry.coordinates as Array<[number, number]>;
        const particleCount = feature.properties.normalized > 0.45 ? 2 : 1;
        for (let particle = 0; particle < particleCount; particle += 1) {
          const seed = stableOffset(`${feature.properties.id}-${particle}`);
          const progress = reducedMotion ? 0.64 : (now / 6200 + seed) % 1;
          const index = Math.min(coordinates.length - 2, Math.max(1, Math.floor(progress * (coordinates.length - 1))));
          features.push({
            type: "Feature",
            properties: {
              bearing: bearingAt(coordinates, index),
              normalized: feature.properties.normalized,
            },
            geometry: { type: "Point", coordinates: coordinates[index] },
          });
        }
      });
      source.setData({ type: "FeatureCollection", features });
      if (!reducedMotion) animationRef.current = requestAnimationFrame(updateParticles);
    };
    updateParticles(performance.now());
    return () => {
      if (animationRef.current !== null) cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    };
  }, [flowFeatures, mapLoaded]);

  return (
    <div className="map-wrap">
      <div ref={containerRef} className="map-canvas" aria-label="Interactive Chennai airport origin-destination map" />
      <div className="map-tools" aria-label="Map tools">
        <button
          className="icon-button map-tool"
          type="button"
          aria-label="Fit Chennai study area"
          onClick={() => mapRef.current?.fitBounds(geojsonBounds(data), { padding: 44, duration: 240 })}
        >
          <Focus aria-hidden="true" />
        </button>
      </div>
      <div className="map-instruction"><span className="live-dot" />Click a zone to isolate its airport flow</div>
      {hoveredRegion && (
        <div className="hover-label">{lookup.get(hoveredRegion)?.shortName ?? hoveredRegion}</div>
      )}
      {hoveredFlow && (
        <div className="flow-tooltip" role="tooltip">
          <span>{lookup.get(hoveredFlow.origin)?.shortName ?? hoveredFlow.origin}</span>
          <b aria-hidden="true">→</b>
          <span>{lookup.get(hoveredFlow.destination)?.shortName ?? hoveredFlow.destination}</span>
          <strong>{compactTrips(hoveredFlow.trips)} <small>trips / weekday</small></strong>
        </div>
      )}
    </div>
  );
}

